
import {
  getTeamsFromHTMLOutput,
  getTeamStatsFromHTMLOutput,
} from '@/util/Parsing/Teams';

import {
  getPlayersFromHTMLOutput,
  getPlayerStatsFromHTMLOutput
} from '@/util/Parsing/Players';

import { getGameEventsFromHTMLOutput } from '@/util/Parsing/Events';

import { 
  getDateFromHTMLOutput,
  getGameOfficialsFromHTMLOutput,
  getThreeStarsFromHTMLOutput,
  getVenueStatsFromHTMLOutput
} from '@/util/Parsing/GameInfo';


const GAME_SETTINGS = {};

export function parseGameHTML(html, isPlayoffs) {
  // Set global settings
  GAME_SETTINGS.GAME_TYPE = isPlayoffs ? 'Playoff' : 'Regular Season';

  // Get Teams
  const teams = getTeamsFromHTMLOutput(html);

  // Get Team Stats
  const teamFinalStats = getTeamStatsFromHTMLOutput(html);

  // Get Players
  const players = getPlayersFromHTMLOutput(html);

  // Get Player Stats
  const playerFinalStats = getPlayerStatsFromHTMLOutput(html);

  // Get Events
  const events = getGameEventsFromHTMLOutput(html, teams);

  // Get Date
  const date = getDateFromHTMLOutput(html);

  // Get Three Stars
  const threeStars = getThreeStarsFromHTMLOutput(html);

  // Get Refs
  const officials = getGameOfficialsFromHTMLOutput(html);

  // Get Rink, Income and Attendance
  const venue = getVenueStatsFromHTMLOutput(html);

  // Get Periods
  const periods = createGamePeriodDataFromEvents(events);

  // Calculate Faceoff Wins
  const foWins = calculateTeamFaceoffWinsTotal(teams, playerFinalStats.skaters);

  return mapAndCombine({
    date,
    events,
    foWins,
    officials,
    periods,
    players,
    playerFinalStats,
    teams,
    teamFinalStats,
    threeStars,
    venue,
  })
}

function calculateTeamFaceoffWinsTotal(teams, skaters) {
  const output = {
    [teams.home]: 0,
    [teams.visitor]: 0
  }

  Object.values(skaters).forEach(skater => {
    output[skater.team] += skater.faceoffsWon;
  })

  return output;
}

function mapAndCombine({ teams, teamFinalStats, players, threeStars, playerFinalStats, foWins, ...other }) {
  // Teams
  const mappedTeams = {
    ...teams,
    [teams.home]: {
      ...teams[teams.home],
      stats: {
        live: createStartingTeamStats(),
        final: {
          ...teamFinalStats.home,
          faceoffWins: foWins[teams.home]
        }
      }
    },
    [teams.visitor]: {
      ...teams[teams.visitor],
      stats: {
        live: createStartingTeamStats(),
        final: {
          ...teamFinalStats.visitor,
          faceoffWins: foWins[teams.visitor]
        }
      }
    }
  };

  // Players
  const mappedSkaters = Object.values(playerFinalStats.skaters)
    .map(skater => {
      const keys = Object.keys(skater).filter(stat => !['id', 'name', 'team'].includes(stat));

      if(!players[skater.id]){
        console.warn(`Player ${skater.name} not found in main skater obj`)
        return {
          id: skater.id,
          name: skater.name,
          team: skater.team,
          stats: {
            final: keys.reduce((obj, key) => ({ ...obj, [key]: skater[key] }), {}),
            live: createStartingSkaterStats(),
          }
        }
      }

      return {
        ...players[skater.id],
        stats: {
          final: keys.reduce((obj, key) => ({ ...obj, [key]: skater[key] }), {}),
          live: createStartingSkaterStats(),
        }
      }
    })

  // Goalies
  const mappedGoalies = Object.values(playerFinalStats.goalies)
    .map(goalie => {
      const keys = Object.keys(goalie).filter(stat => !['id', 'name', 'team'].includes(stat));

      if(!players[goalie.id]){
        console.warn(`Goalie ${goalie.name} not found in main skater obj`)
        return {
          id: goalie.id,
          name: goalie.name,
          team: goalie.team,
          stats: {
            final: keys.reduce((obj, key) => ({ ...obj, [key]: goalie[key] }), {}),
            live: createStartingGoalieStats(),
          }
        }
      }

      return {
        ...players[goalie.id],
        stats: {
          final: keys.reduce((obj, key) => ({ ...obj, [key]: goalie[key] }), {}),
          live: createStartingGoalieStats(),
        }
      }
    })

    // Turn names of three stars into IDs
  const mappedThreeStars = threeStars.map(star => {
    const player = Object.values(players).find(player => player.name === star);
    return player.id;
  })

  return {
    ...other,
    teams: mappedTeams,
    skaters: mappedSkaters,
    threeStars: mappedThreeStars,
    goalies: mappedGoalies,
  }
}


function createStartingTeamStats(){
  return {
    blockedShots: 0,
    faceoffWins: 0,
    hits: 0,
    pims: 0,
    pk: { kills: 0, attempts: 0 },
    pp: { goals: 0, attempts: 0 },
    goals: { total: 0 },
    shots: { total: 0 },
  }
}

function createStartingSkaterStats(){
  return {
    goals: 0,
    assists: 0,
    points: 0,
    pims: 0,
    shots: 0,
    hits: 0,
    shotsBlocked: 0,
    giveaways: 0,
    takeaways: 0,
    faceoffsWon: 0,
    faceoffsTaken: 0,
  }
}

function createStartingGoalieStats(){
  return {
    saves: 0,
    shots: 0,
    pims: 0,
  }
}

function createGamePeriodDataFromEvents(events){
  const periods = {};

  Object.values(events).forEach((item, idx) => {
    const number = idx + 1;

    // If REG season, OT is 5 mins
    const length = getGameType() === 'Playoff' || number <= 3 ? 1200 : 300;

    // Get a key that all periods will have (opening faceoff)
    const time = item[1].time;

    periods[time.key] = {
      key: time.key,
      name: time.period,
      number,
      isOT: number > 3,
      isShootout: time.key === 'shootout',
      length,
    };
  })

  return periods;
}

export const getGameType = () => GAME_SETTINGS.GAME_TYPE;

export default {
  getGameType,
  parseGameHTML
}